import {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {DataGrid} from '@mui/x-data-grid';
import {Stack} from '@mui/material';
import Button from '@mui/material/Button';
import {NavLink as RouterLink, Outlet, useNavigate} from 'react-router-dom';
import {ALL_PERFORMERS_CONTENT_AND_ACCREDITATION, ALL_USERS, UPDATE_USER} from '../queries/GraphQLQueries';
import CustomAlert from '../components/CustomAlert';
import Loader from "../components/Loader";

const buttonContainerStyles = {
    margin: '0.5rem',
    marginTop: '0rem',
};

const columns = [
    { field: 'givenName', headerName: 'Given Name', width: 100, editable: true },
    { field: 'familyName', headerName: 'Family Name', width: 120, editable: true },
    { field: 'displayName', headerName: 'Display Name', width: 120, editable: true },
    { field: 'email', headerName: 'E-mail', width: 300, editable: false },
    { field: 'canTeach', headerName: 'Teach', width: 80, type: 'boolean', editable: true },
    { field: 'canContentManage', headerName: 'Content Manage', width: 120, type: 'boolean', editable: true },
    { field: 'canTrafficManage', headerName: 'Traffic Manage', width: 120, type: 'boolean', editable: true },
    { field: 'canCustomerSupport', headerName: 'Customer Support', width: 140, type: 'boolean', editable: true },
    { field: 'canLiveOps', headerName: 'Live Operations', width: 120, type: 'boolean', editable: true },
    { field: 'canQA', headerName: 'QA', width: 60, type: 'boolean', editable: true },
    { field: 'canAdmin', headerName: 'Admin', width: 80, type: 'boolean', editable: true },
    { field: 'deleteRow', renderCell: RenderDeleteButton, headerName: 'Delete', width: 100, editable: false }
];

function RenderDeleteButton(params) {

    const navigate = useNavigate();

    const handleDeleteButtonClick = () => {
        navigate('delete', {
            state: {
                userId: params.row.id,
                userName: params.row.displayName
            }
        });
    };

    return (
        <Button variant="contained" size="small" style={{ marginLeft: 8 }} onClick={handleDeleteButtonClick}>
            Delete
        </Button>
    )
}


export default function DomainUserView() {
    const { loading, error, data } = useQuery(ALL_USERS);
    const [mutateRow, { updatedData, loadingMutate, mutateError }] = useMutation(UPDATE_USER, {
        refetchQueries: [
            { query: ALL_PERFORMERS_CONTENT_AND_ACCREDITATION }, 'GetAllPerformersContentAndAccreditation'
        ]
    });
    
    const [alertMessage, setAlertMessage] = useState(null);
    const handleCloseCustomAlert = () => {
        setAlertMessage(null);
    };

    const processRowUpdate = function (newRow, oldRow) {
        setAlertMessage({ children: 'Saving...', severity: 'success' });

        return new Promise(function (resolve, reject) {
            mutateRow({
                variables: {
                    id: newRow.id,
                    givenName: newRow.givenName,
                    familyName: newRow.familyName,
                    displayName: newRow.displayName,
                    canTeach: Boolean(newRow.canTeach),
                    canContentManage: Boolean(newRow.canContentManage),
                    canTrafficManage: Boolean(newRow.canTrafficManage),
                    canCustomerSupport: Boolean(newRow.canCustomerSupport),
                    canLiveOps: Boolean(newRow.canLiveOps),
                    canQA: Boolean(newRow.canQA),
                    canAdmin: Boolean(newRow.canAdmin)
                }
            })
                .then(() => {
                    resolve(newRow);
                    setAlertMessage({ children: 'Saved', severity: 'success' });
                }).catch(() => {
                    reject();
                });
        });
    }

    const processRowUpdateError = function (error) {
        console.log('Error on processing row ' + error);
        setAlertMessage({ children: error?.message, severity: 'error' });
    }

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return `Request Error! ${error.message}`;
    }

    // This style freezes the top bar along with data grid header row when user scrolls down
    return <Stack style={{height: 'calc(100vh - 16px)'}}>
        <Stack sx={buttonContainerStyles}>
            <Button variant='contained' style={{width: '150px'}} to='new' component={RouterLink}>Create User</Button>
        </Stack>
        <DataGrid
            initialState={{
                sorting: {sortModel: [{field: 'givenName', sort: 'asc'}]},
            }}
            rows={data && data.users}
            columns={columns}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={processRowUpdateError}
            experimentalFeatures={{newEditingApi: true}}
            editMode='row' />

        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
        <Outlet />
    </Stack>;
}
